// pages/BankInfoSettings.js
import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Label,
  Input,
  Button,
  Spinner,
  FormGroup,
} from "reactstrap";
import Select from "react-select";
import { withTranslation } from "react-i18next";

import BreadcrumbAboveImage from "components/Common/BreadcrumbAboveImage";
import axiosInstance from "utils/axiosInstance";

import { bankList } from "./BankInfoUtils";

import bank10 from "assets/images/info/bank10.png";

const BankInfoSettings = ({ t }) => {
  document.title = "銀行帳戶設定 | TheChart.AI";


  /*-------------------------------
  /* BankInfo Form States
  /*-------------------------------*/

  const [formData, setFormData] = useState({
    bankCode: "",
    accountNumber: "",
    accountName: "",
    idNumber: "",
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const bankOptions = bankList.map((bank) => ({
    value: bank.code,
    label: `${bank.code} - ${bank.name}`,
  }));


  /*-------------------------------
  /* Preferred Currency States
  /*-------------------------------*/

  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const currencyOptions = [
    { value: 'USD', label: 'USD' },
    { value: 'TWD', label: 'TWD' },
    { value: 'JPY', label: 'JPY' },
    { value: 'EUR', label: 'EUR' },
    { value: 'GBP', label: 'GBP' },
  ];



  /*-------------------------------
  /* Fetch
  /*-------------------------------*/

  const fetchBankInfo = async () => {
    try {
      const { data } = await axiosInstance.get("/knovia/myInfo/bankInfo");
      console.log('info fetched:', data);
      setFormData(data);
      if(data.preferredCurrency){
        const preferredCurrency = data.preferredCurrency;
        setSelectedCurrency({ value: preferredCurrency, label: preferredCurrency });
      }
    } catch (error) {
      console.error("Failed to load bank info", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBankInfo();
  }, []);




  /*-------------------------------
  /* Change and Save Form
  /*-------------------------------*/

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    const { bankCode, accountNumber, accountName } = formData;
    console.log('formData:', formData);
    console.log('selectedCurrency:', selectedCurrency);
    if (!bankCode || !accountNumber || !accountName) {
      alert("請完整填寫銀行資訊");
      return;
    }

    setIsSaving(true);
    try {
      await axiosInstance.post("/knovia/myInfo/bankInfo", 
        { preferredCurrency: selectedCurrency.value, bankCode, accountNumber, accountName }
      );
      alert("儲存成功！");
    } catch (err) {
      console.error(err);
      alert("儲存失敗，請稍後再試");
    } finally {
      setIsSaving(false);
    }
  };






  return (
    <div className="page-content"
      style={{ 
        backgroundImage: `url(${bank10})`,
        backgroundSize: "cover"
      }}
    >
      <Container fluid>
        {/*<BreadcrumbAboveImage title={t("我的 AI 應用主頁")} breadcrumbItem={t("銀行帳戶設定")} />*/}
        <BreadcrumbAboveImage
          title={t("銀行帳戶設定")}
          breadcrumbItems={[
            { title: t("我的 AI 應用主頁"), to: "/myInfo/dashboard" },
            { title: t("點數銀行主頁"), to: "/myInfo/pointsBank" },
            { title: t("銀行帳戶設定") }, // no link for the active one
          ]}
        />

        <Card style={{ minHeight: "30rem" }}>
          <CardBody>
            <CardTitle className="mb-4">銀行帳戶資訊</CardTitle>

            {isLoading ? (
              <Spinner size="sm" />
            ) : (
              <>
                <Row className="mb-3">
                  <Col md={6}>
                    <FormGroup>
                      <Label>銀行代碼</Label>
                      <Select
                        options={bankOptions}
                        value={bankOptions.find((opt) => opt.value === formData.bankCode) || null}
                        onChange={(selectedOption) =>
                          setFormData((prev) => ({ ...prev, bankCode: selectedOption?.value || "" }))
                        }
                        placeholder="請選擇銀行"
                        isClearable
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>銀行帳號</Label>
                      <Input
                        type="text"
                        value={formData.accountNumber}
                        onChange={handleChange("accountNumber")}
                        placeholder="請輸入銀行帳號"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <FormGroup>
                      <Label>戶名</Label>
                      <Input
                        type="text"
                        value={formData.accountName}
                        onChange={handleChange("accountName")}
                        placeholder="請輸入戶名（需與銀行一致）"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>身分證字號 / 統編（選填）</Label>
                      <Input
                        type="text"
                        value={formData.idNumber}
                        onChange={handleChange("idNumber")}
                        placeholder="例如 A123456789 或 12345678"
                      />
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <Label>{t("預設使用貨幣")}</Label>
                    <Select
                      options={currencyOptions}
                      value={selectedCurrency}
                      onChange={(e) => {
                        console.log('e.value:', e.value);
                        setSelectedCurrency(prev => ({label: e.value, value: e.value}));
                      }}
                      placeholder={t("請選擇")}
                      isDisabled={isSaving || isLoading}
                    />
                  </Col>

                </Row>

                <div style={{ textAlign: "right" }}>
                  <Button
                    color="primary"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? "儲存中..." : "儲存銀行資訊"}
                  </Button>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default withTranslation()(BankInfoSettings);
